import { compact } from '@direct-frontend/stdlib';
import { v4 } from 'uuid';
import { getFilterList } from '../getFilterList';
import { BaseFilter, FiltersDeclaration, InferFilterItemFromFilter } from '../../types/Filters';

/**
 * Делит на подстроки по символу, учитывая экранирование этого символа.
 * В процессе убирает экранирование этого символа
 * @param string - строка для разбиения
 * @param splitCharacter - символ разбиения
 * @returns массив строк
 */
export function splitByCharacter(string: string, splitCharacter: string) {
    const result = [];
    let currentPart = '';
    let escaped = false;

    for (let i = 0; i < string.length; i++) {
        const currentCharacter = string[i] ?? '';

        if (escaped) {
            currentPart += currentCharacter === splitCharacter ? currentCharacter : `\\${currentCharacter}`;
            escaped = false;
            continue;
        }

        if (currentCharacter === '\\') {
            escaped = true;
            continue;
        }

        if (currentCharacter === splitCharacter) {
            result.push(currentPart);
            currentPart = '';
            continue;
        }

        currentPart += currentCharacter;
    }

    if (currentPart) {
        result.push(currentPart);
    }

    return result;
}

/**
 * Парсит строку с фильтрами в FilterItem[].
 * Работает только для фильтров со строчными значениями – с массивом строк или одиночной строкой (или энамов).
 * Подходит для фильтров на гридах, так как они преобразуются к строке для использования в query
 * @param filtersDeclaration - описание фильтров
 * @param filterItemsString - строка с элементами фильтрации
 * @typeParam FilterType - тип фильтра
 * @returns массив FilterItem
 * @kind helper
 */
export function parseFilterItems<FilterType extends BaseFilter>(
    filtersDeclaration: FiltersDeclaration<FilterType>,
    filterItemsString: string
): InferFilterItemFromFilter<FilterType>[] {
    const filters = getFilterList(filtersDeclaration);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- помогаем вывести типы
    return compact(
        splitByCharacter(filterItemsString, '|').map(filterItemString => {
            const [filterDescription, operator, ...rest] = filterItemString.split(' ');
            const valueString = rest.join(' ');

            if (!filterDescription || !operator || !valueString) {
                return null;
            }

            const [filterId, subFilterId] = filterDescription.split('.');
            const filter = filters.find(filter => filter.id === filterId);

            if (!filter) {
                return null;
            }

            const filterPart = filter.reconcile({
                subFilterId,
                operator,
                value: splitByCharacter(valueString, ',').map(value => value.replace(/\\\\/g, '\\')),
            });

            return filterPart ? { id: v4(), wasAutoFocused: true, ...filterPart, filterId } : null;
        })
    ) as InferFilterItemFromFilter<FilterType>[];
}
